/**
 * Presets — STARTER TEMPLATES, not a runtime registry.
 *
 * Surfaced in the White-Label Editor as "Apply preset" buttons. Editing a
 * preset does NOT affect any existing tenant — only the next time someone
 * clicks the button. Adding a new preset is purely additive (no migrations).
 *
 * One exception keeps this file on the runtime path: a tenant may still carry
 * the legacy `tenant_settings.brand_id` naming a preset, with sparse or empty
 * `brand_overrides`. The app paints such a tenant with the preset UNDER its
 * overrides (`combinePresetAndOverrides` below, run by BrandResolver), and the backend has
 * to see the same brand when it brands an email — so this module is kept
 * free of React and of the rest of the brand layer, and
 * `scripts/brand/build-presets-manifest.mjs` compiles exactly this file (plus
 * `../types`) to write `config/brand_presets.json` for the backend.
 * Regenerate after editing a preset or `presetAsOverrides`; `--check` is the
 * gate.
 */

import type { BrandConfig, BrandOverrides } from '../types';
import m2Preset from './m2';
import brijPreset from './brij';

export const presets: Record<string, BrandConfig> = {
  m2: m2Preset,
  brij: brijPreset,
};
export type PresetId = keyof typeof presets;

export function listPresetIds(): string[] {
  return Object.keys(presets);
}
export function getPreset(id: string): BrandConfig | null {
  return presets[id] ?? null;
}

// Convert a preset's `BrandConfig` into a `BrandOverrides` blob suitable for
// `setActiveBrand` / persistence. Accepts either a preset id or the resolved
// BrandConfig directly. Centralized here so adding a new BrandOverrides field
// touches one file instead of three.
export function presetAsOverrides(idOrPreset: string | BrandConfig): BrandOverrides | null {
  const p = typeof idOrPreset === 'string' ? getPreset(idOrPreset) : idOrPreset;
  if (!p) return null;
  return {
    name: p.name,
    tagline: p.tagline,
    companyName: p.companyName,
    companyUrl: p.companyUrl,
    pageTitle: p.pageTitle,
    logo: p.logo,
    logoDark: p.logoDark,
    icon: p.icon,
    iconDark: p.iconDark,
    favicon: p.favicon,
    conciergeIcon: p.conciergeIcon,
    conciergeIconDark: p.conciergeIconDark,
    anniversaryIcon: p.anniversaryIcon,
    anniversaryIconDark: p.anniversaryIconDark,
    anniversaryLogo: p.anniversaryLogo,
    anniversaryLogoDark: p.anniversaryLogoDark,
    colors: p.colors,
    fonts: p.fonts,
    lightMode: p.lightMode,
    darkMode: p.darkMode,
    loginEyebrow: p.loginEyebrow,
    appName: p.appName,
    loginFeatures: p.loginFeatures,
    terminology: p.terminology,
    agentColors: p.agentColors,
    agentAvatars: p.agentAvatars,
    /* Visual treatments — must be included so "Apply M² preset" in the
       editor actually persists the semicircle + chat-tint toggles. Without
       these the PATCH would omit them and the saved tenant would silently
       fall back to the default (both OFF). */
    homeSemicircle: p.homeSemicircle,
    chat: p.chat,
  };
}

/**
 * Deep-merge two BrandOverrides — `top` wins on conflicts. Objects
 * are merged one level deep (covers nested groups like `colors`,
 * `fonts`, `chat`, `homeSemicircle`); arrays + primitives replace
 * wholesale. Used to layer tenant brand_overrides on top of a preset
 * base when the tenant has both `brand_id` AND a sparse overrides
 * blob (e.g. team-alpha = m2 preset + {chat.tintByAgent: true}).
 */
function deepMergeOverrides(
  base: BrandOverrides | null,
  top: BrandOverrides | null,
): BrandOverrides | null {
  if (!base) return top;
  if (!top) return base;
  const out: Record<string, unknown> = { ...base };
  for (const key of Object.keys(top)) {
    const b = (base as Record<string, unknown>)[key];
    const t = (top as Record<string, unknown>)[key];
    const bothObj = b && t &&
      typeof b === 'object' && typeof t === 'object' &&
      !Array.isArray(b) && !Array.isArray(t);
    out[key] = bothObj ? { ...(b as object), ...(t as object) } : t;
  }
  return out as BrandOverrides;
}

/**
 * Layer the preset (from legacy `brand_id`) under the tenant's
 * brand_overrides. Returns whichever is non-empty when only one is
 * available. Why both: tenants like team-alpha have `brand_id='m2'`
 * + a tiny `brand_overrides` ({chat.tintByAgent: true, homeSemicircle:
 * {enabled: true}}). Returning only the tiny overrides paints the
 * default brand with two extra visual treatments — visibly broken.
 * The preset is the BASE; tenant overrides paint on top.
 */
export function combinePresetAndOverrides(
  brandId: string | null | undefined,
  overrides: BrandOverrides | null,
): BrandOverrides | null {
  const preset = brandId ? getPreset(brandId) : null;
  const presetOverrides = preset ? presetAsOverrides(preset) : null;
  const hasOverrides = overrides && Object.keys(overrides).length > 0;
  if (presetOverrides && hasOverrides) {
    return deepMergeOverrides(presetOverrides, overrides);
  }
  if (hasOverrides) return overrides;
  return presetOverrides;
}
